import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { appSignupLink } from '@/utils/signupUrl'
import { trialCopy } from '@/utils/trialCopy'

const questions = [
  {
    number: 1,
    question: 'Where is my client data stored?',
    whatToLookFor: 'UK or EU data centres. Avoid vendors who can\'t tell you exactly where data sits.',
    redFlag: 'Vague answers like "in the cloud" or data stored outside the UK.',
  },
  {
    number: 2,
    question: 'Is my client data used to train your AI?',
    whatToLookFor: 'A clear "no." Your client data should never be used to improve their AI models.',
    redFlag: 'Terms that allow them to use your data for "product improvement" or "model training."',
  },
  {
    number: 3,
    question: 'Can I cancel monthly or am I locked into an annual contract?',
    whatToLookFor: 'Monthly rolling contracts with no exit fees. You should be able to cancel with a click.',
    redFlag: 'Annual contracts with auto-renewal, or cancellation penalties.',
  },
  {
    number: 4,
    question: 'Do I need a sales call to get started?',
    whatToLookFor: 'Self-serve sign up. If the product is good, you shouldn\'t need to be sold it.',
    redFlag: 'Mandatory "discovery calls" or "onboarding consultations" before you can try anything.',
  },
  {
    number: 5,
    question: 'Is there a free trial?',
    whatToLookFor: 'A genuine free trial with full access, transparent cancellation, and no long-term lock-in.',
    redFlag: '"Free demo" (not the same as a trial) or trials that require payment details.',
  },
  {
    number: 6,
    question: 'What happens to my data if you get acquired or shut down?',
    whatToLookFor: 'A clear data export process and a commitment to advance notice.',
    redFlag: 'No answer, or terms that give them broad rights over your data.',
  },
  {
    number: 7,
    question: 'Is the pricing transparent?',
    whatToLookFor: 'Pricing on the website. You should know what you\'ll pay before talking to anyone.',
    redFlag: '"Contact us for pricing" on every tier, which usually means it\'s expensive.',
  },
  {
    number: 8,
    question: 'Do I need an implementation project?',
    whatToLookFor: 'Same-day setup. If it takes 6 weeks to implement, it\'s probably too complex.',
    redFlag: 'Paid implementation, dedicated project managers, or "typical onboarding takes 4-8 weeks."',
  },
  {
    number: 9,
    question: 'Who built this, advisers or engineers?',
    whatToLookFor: 'A team that understands advice workflows, compliance, and the FCA. Domain expertise matters.',
    redFlag: 'A generic AI company that\'s pivoted to financial services from another sector.',
  },
  {
    number: 10,
    question: 'Can I customise it to my firm?',
    whatToLookFor: 'Custom templates, your branding, your tone of voice. The output should look like yours.',
    redFlag: 'One-size-fits-all templates with no customisation.',
  },
]

export default function AIBuyersChecklistPage() {
  const utm = useUtmPassthrough()
  const signup = appSignupLink({ params: utm })
  return (
    <>
      <SEO
        title="AI Buyer's Checklist for UK Financial Advisers | WealthSpace"
        description="10 questions every financial adviser should ask before buying AI software. Free checklist, no sign-up required."
        keywords="AI software for financial advisers, what to look for in AI tools, financial adviser technology checklist, AI vendor evaluation"
        url="/ai-buyers-checklist"
      />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 text-center">
        <div className="max-w-[700px] mx-auto">
          <p className="text-sm font-semibold text-accent-teal tracking-wide uppercase mb-4">Free resource</p>
          <h1 className="font-serif text-[clamp(36px,5vw,52px)] text-slate-900 mb-6">
            10 questions to ask any AI vendor
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Thinking about AI for your practice? Use this checklist before you buy anything, from us or anyone else.
          </p>
        </div>
      </section>

      {/* Questions */}
      <section className="pb-28 px-6">
        <div className="max-w-[800px] mx-auto space-y-6">
          {questions.map((q) => (
            <div key={q.number} className="bg-white border border-slate-200 rounded-xl p-8 shadow-card">
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-accent-teal text-white flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                  {q.number}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-serif font-medium text-slate-900 mb-4">{q.question}</h2>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-accent-teal flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                      <div>
                        <span className="text-xs font-semibold text-accent-teal uppercase tracking-wider">What to look for</span>
                        <p className="text-[15px] text-slate-600 leading-relaxed mt-1">{q.whatToLookFor}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      <div>
                        <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Red flag</span>
                        <p className="text-[15px] text-slate-600 leading-relaxed mt-1">{q.redFlag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="font-serif text-3xl text-slate-900 mb-4">
            Like what you see?
          </h2>
          <p className="text-slate-600 mb-3">
            WealthSpace was built to meet this checklist. But don't take our word for it.
          </p>
          <p className="text-slate-600 mb-8">
            Try it yourself. {trialCopy.oneLine}.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button href={signup.href} external={signup.external} size="lg">
              Start Free Trial
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Button>
            <Button href="/demo" variant="outline" size="lg">
              Try the Demo First
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
